// src/services/cacheService.ts
import { RestaurantMenuData } from "../types";

interface CacheItem {
  data: RestaurantMenuData;
  timestamp: number;
  expiresAt: number;
  version: string;
}

class CacheService {
  private cacheName = "restaurant-menu-cache";
  private db: IDBDatabase | null = null;
  private dbVersion = 1;
  private defaultTTL = 5 * 60 * 1000; // 5 minutes
  private cacheVersion = "1.0.0";
  // Fallback storage when IndexedDB is unavailable
  private useFallback = false;
  private fallbackStore: Map<string, CacheItem> | null = null;
  private fallbackStorageKey = this.cacheName + "_fallback";

  constructor() {
    // Initialize DB asynchronously; methods will await initDB when needed
    this.initDB();
  }
  private async initDB(): Promise<void> {
    if (this.db || this.useFallback) return;

    const safeOpen = async (): Promise<IDBDatabase | null> => {
      if (!("indexedDB" in window)) return null;
      return new Promise((resolve) => {
        let resolved = false;
        try {
          const req = indexedDB.open(this.cacheName, this.dbVersion);

          req.onsuccess = () => {
            if (!resolved) {
              resolved = true;
              resolve(req.result);
            }
          };

          req.onupgradeneeded = (event) => {
            // Create store if missing during upgrade
            const db = (event.target as IDBOpenDBRequest).result;
            if (!db.objectStoreNames.contains("menuCache")) {
              const store = db.createObjectStore("menuCache", {
                keyPath: "id",
              });
              store.createIndex("expiresAt", "expiresAt", { unique: false });
              store.createIndex("timestamp", "timestamp", { unique: false });
            }
          };

          req.onerror = () => {
            if (!resolved) {
              resolved = true;
              resolve(null);
            }
          };

          // safety timeout: some environments hang on open
          setTimeout(() => {
            if (!resolved) {
              resolved = true;
              try {
                // best-effort abort by closing the request's result if any
              } catch (e) {
                /* ignore */
              }
              resolve(null);
            }
          }, 3000);
        } catch (e) {
          resolve(null);
        }
      });
    };

    const db = await safeOpen();
    if (!db) {
      console.warn(
        "IndexedDB not available; using fallback storage (localStorage/map)",
      );
      this.useFallback = true;
      this.loadFallbackFromStorage();
      return;
    }

    this.db = db;
  }

  private loadFallbackFromStorage() {
    try {
      const raw = localStorage.getItem(this.fallbackStorageKey);
      if (raw) {
        const arr = JSON.parse(raw) as Array<{ id: string } & CacheItem>;
        this.fallbackStore = new Map(
          arr.map((it) => [
            it.id,
            {
              data: it.data,
              timestamp: it.timestamp,
              expiresAt: it.expiresAt,
              version: it.version,
            },
          ]),
        );
      } else {
        this.fallbackStore = new Map();
      }
    } catch (e) {
      this.fallbackStore = new Map();
    }
  }

  private persistFallbackToStorage() {
    try {
      if (!this.fallbackStore) return;
      const arr = Array.from(this.fallbackStore.entries()).map(
        ([id, item]) => ({ id, ...item }),
      );
      localStorage.setItem(this.fallbackStorageKey, JSON.stringify(arr));
    } catch (e) {
      console.warn("Failed to persist fallback cache to localStorage", e);
    }
  }

  async set(
    key: string,
    data: RestaurantMenuData,
    ttl?: number,
  ): Promise<void> {
    await this.initDB();

    const now = Date.now();
    const expiresAt = now + (ttl || this.defaultTTL);

    const cacheItem: CacheItem = {
      data,
      timestamp: now,
      expiresAt,
      version: this.cacheVersion,
    };

    if (this.useFallback || !this.db) {
      if (!this.fallbackStore) this.fallbackStore = new Map();
      this.fallbackStore.set(key, cacheItem);
      this.persistFallbackToStorage();
      return;
    }

    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error("Database not initialized"));
        return;
      }

      const transaction = this.db.transaction(["menuCache"], "readwrite");
      const store = transaction.objectStore("menuCache");
      const request = store.put({ id: key, ...cacheItem });

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  async get(key: string): Promise<RestaurantMenuData | null> {
    await this.initDB();

    if (this.useFallback || !this.db) {
      if (!this.fallbackStore) this.loadFallbackFromStorage();
      if (!this.fallbackStore) return null;
      const item = this.fallbackStore.get(key) as CacheItem | undefined | null;
      if (!item) return null;
      if (Date.now() > item.expiresAt) {
        this.fallbackStore.delete(key);
        this.persistFallbackToStorage();
        return null;
      }
      return item.data;
    }

    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error("Database not initialized"));
        return;
      }

      const transaction = this.db.transaction(["menuCache"], "readonly");
      const store = transaction.objectStore("menuCache");
      const request = store.get(key);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const item = request.result as CacheItem | undefined;

        if (!item) {
          resolve(null);
          return;
        }

        // Check if expired
        if (Date.now() > item.expiresAt) {
          this.delete(key);
          resolve(null);
          return;
        }

        resolve(item.data);
      };
    });
  }

  async delete(key: string): Promise<void> {
    await this.initDB();

    if (this.useFallback || !this.db) {
      if (!this.fallbackStore) this.loadFallbackFromStorage();
      this.fallbackStore?.delete(key);
      this.persistFallbackToStorage();
      return;
    }

    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error("Database not initialized"));
        return;
      }

      const transaction = this.db.transaction(["menuCache"], "readwrite");
      const store = transaction.objectStore("menuCache");
      const request = store.delete(key);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  async clear(): Promise<void> {
    await this.initDB();

    if (this.useFallback || !this.db) {
      if (this.fallbackStore) {
        this.fallbackStore.clear();
        this.persistFallbackToStorage();
      }
      return;
    }

    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error("Database not initialized"));
        return;
      }

      const transaction = this.db.transaction(["menuCache"], "readwrite");
      const store = transaction.objectStore("menuCache");
      const request = store.clear();

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  async getStats(): Promise<{ size: number; keys: string[] }> {
    await this.initDB();

    if (this.useFallback || !this.db) {
      if (!this.fallbackStore) this.loadFallbackFromStorage();
      const keys = this.fallbackStore
        ? Array.from(this.fallbackStore.keys())
        : [];
      return { size: keys.length, keys };
    }

    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error("Database not initialized"));
        return;
      }

      const transaction = this.db.transaction(["menuCache"], "readonly");
      const store = transaction.objectStore("menuCache");
      const keys: string[] = [];

      const request = store.openCursor();

      request.onerror = () => reject(request.error);
      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          keys.push(cursor.key as string);
          cursor.continue();
        } else {
          resolve({ size: keys.length, keys });
        }
      };
    });
  }

  async isOnline(): Promise<boolean> {
    return navigator.onLine;
  }
}

export const cacheService = new CacheService();
